import { useCallback } from 'react';
import {
  useCreateScenarioMutation,
  useDeleteScenarioMutation,
  useScenariosQuery,
  useUpdateScenarioMutation,
} from '../api/queries';
import type { ScenarioRecord } from './scenarioStore';

/**
 * React-friendly wrapper over server-backed scenario persistence. See
 * scenarioStore.ts for the actual HTTP calls this sits on top of.
 */
export function useScenarios(): {
  scenarios: ScenarioRecord[];
  isLoading: boolean;
  refresh: () => void;
  create: (record: Omit<ScenarioRecord, 'id' | 'ownerUserId' | 'createdAt' | 'updatedAt'>) => Promise<ScenarioRecord>;
  update: (id: string, patch: Partial<Omit<ScenarioRecord, 'id' | 'ownerUserId' | 'createdAt'>>) => void;
  remove: (id: string) => void;
} {
  const query = useScenariosQuery();
  const createMutation = useCreateScenarioMutation();
  const updateMutation = useUpdateScenarioMutation();
  const deleteMutation = useDeleteScenarioMutation();

  const refresh = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query identity changes every render; refetch is stable enough for a manual nudge
  }, []);

  // Genuinely needs its return value — ScenarioListPage navigates to the new
  // scenario's id — so this is the one operation callers must await.
  const create = useCallback(
    (record: Omit<ScenarioRecord, 'id' | 'ownerUserId' | 'createdAt' | 'updatedAt'>) =>
      createMutation.mutateAsync(record),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation identity changes every render
    [],
  );

  // Fire-and-forget, matching every existing call site's usage exactly — the
  // query invalidation on success is what refreshes the visible list.
  const update = useCallback(
    (id: string, patch: Partial<Omit<ScenarioRecord, 'id' | 'ownerUserId' | 'createdAt'>>) => {
      updateMutation.mutate({ id, patch });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation identity changes every render
    [],
  );

  const remove = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation identity changes every render
    [],
  );

  return { scenarios: query.data ?? [], isLoading: query.isLoading, refresh, create, update, remove };
}
